'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { processError, logError, ApplicationError } from '@/utils/errorHandling';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: ApplicationError) => void;
  showErrorDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: ApplicationError;
  errorId?: string;
}

/**
 * Error boundary component that catches JavaScript errors anywhere in the child component tree
 * Provides user-friendly error display and error reporting capabilities
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const applicationError = processError(error);
    const errorId = `error-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    return {
      hasError: true,
      error: applicationError,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const applicationError = this.state.error;

    if (applicationError) {
      logError(applicationError);

      // Call optional error handler
      this.props.onError?.(applicationError);

      // Log additional React error info
      console.error('React Error Info:', {
        componentStack: errorInfo.componentStack,
        errorBoundary: this.constructor.name,
        errorId: this.state.errorId,
      });
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorId: undefined });
  };

  private handleGoHome = () => {
    const pathname = window.location.pathname;

    let homePath = '/';
    if (pathname.startsWith('/manage/')) {
      homePath = '/manage';
    }

    if (pathname !== homePath) {
      window.location.href = homePath;
    }
  };

  private handleReportError = () => {
    if (this.state.error && this.state.errorId) {
      // In a real application, this would send the error to a logging service
      const errorReport = {
        errorId: this.state.errorId,
        message: this.state.error.message,
        category: this.state.error.category,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      console.log('Error Report:', errorReport);

      // You could integrate with services like Sentry, LogRocket, etc.
      // Sentry.captureException(this.state.error.originalError, {
      //   tags: { errorId: this.state.errorId },
      //   extra: errorReport,
      // });
    }
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorId } = this.state;
      const { showErrorDetails = false } = this.props;

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            {/* Error Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </div>

            {/* Error Message */}
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h1>

            <p className="text-gray-600 mb-6">
              {error?.userFriendlyMessage || 'We encountered an unexpected error. Please try again.'}
            </p>

            {/* Error Details (Development only) */}
            {showErrorDetails && error && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Error Details</h3>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>
                    <strong>ID:</strong> {errorId}
                  </div>
                  <div>
                    <strong>Category:</strong> {error.category}
                  </div>
                  <div>
                    <strong>Message:</strong> {error.message}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button onClick={this.handleRetry} className="w-full flex items-center justify-center gap-2">
                <RotateCcw className="w-4 h-4" />
                Try Again
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={this.handleGoHome}
                  className="flex-1 flex items-center justify-center gap-2">
                  <Home className="w-4 h-4" />
                  Go Home
                </Button>

                {showErrorDetails && (
                  <Button variant="outline" onClick={this.handleReportError} className="flex-1">
                    Report Error
                  </Button>
                )}
              </div>
            </div>

            {/* Help Text */}
            <p className="text-xs text-gray-500 mt-6">If this problem persists, please contact support.</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook for creating error boundaries programmatically
 * Useful for wrapping specific components with error handling
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>,
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}
